/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import Fuse from 'fuse.js';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupIcon from '@mui/icons-material/Group';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Avatar,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
} from '@mui/material';

import { CIColors } from 'shared';

import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { UserApiService } from '../../../../services/UserApiService';
import { useSelectEntityModalStyles } from '../../../../styles/selectEntityModal';
import { useInviteMemberStyles } from '../InviteMember/InviteMember.style';

type Props = {
  users: { id: string; firstName: string; lastName: string; email: string }[];
  teams: { identifier: string; displayName: string }[];
  open: boolean;
  onSuccess: (values: { userIds: string[]; teamIdentifiers: string[] }) => void;
  onCancel: () => void;
};

const fuseOptions = { shouldSort: true, ignoreLocation: true, threshold: 0 };

export const MemberPicker = memo<Props>(({ users, teams, open, onSuccess, onCancel }) => {
  const { t } = useTranslation();

  const classes = useInviteMemberStyles();
  const modalClasses = useSelectEntityModalStyles();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<{ type: 'user' | 'team'; id: string }[]>([]);

  // const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // const [selectedTeamIdentifiers, setSelectedTeamIdentifiers] = useState<string[]>([]);

  const visibleItems = useMemo(() => {
    const visibleUsers = searchQuery
      ? new Fuse(users, { ...fuseOptions, keys: ['firstName', 'lastName'] }).search(searchQuery).map(r => r.item)
      : users;
    const visibleTeams = searchQuery
      ? new Fuse(teams, { ...fuseOptions, keys: ['displayName', 'identifier'] }).search(searchQuery).map(r => r.item)
      : teams;
    return [
      ...visibleUsers.map(user => ({ type: 'user' as const, ...user })),
      ...visibleTeams.map(team => ({ type: 'team' as const, id: team.identifier, ...team })),
    ];
  }, [users, teams, searchQuery]);

  const handleToggle = useCallback(
    (item: (typeof visibleItems)[number]) => () =>
      setSelectedItems(prevState => {
        const ids = prevState.filter(({ type, id }) => !(type === item.type && id === item.id));
        if (ids.length === prevState.length) {
          ids.push(item);
        }
        return ids;
      }),
    [setSelectedItems],
  );

  const clear = useCallback(() => {
    setSearchQuery('');
    setSelectedItems([]);
  }, [setSearchQuery, setSelectedItems]);

  const handleSubmit = useCallback(() => {
    clear();
    onSuccess({
      userIds: selectedItems.filter(i => i.type === 'user').map(i => i.id),
      teamIdentifiers: selectedItems.filter(i => i.type === 'team').map(i => i.id),
    });
  }, [clear, onSuccess, selectedItems]);

  const handleClose = useCallback(() => {
    clear();
    onCancel();
  }, [clear, onCancel]);

  return (
    <HUModal2
      open={open}
      onClose={handleClose}
      size="medium"
      title={t('organizationAdmin:teams.add_members')}
      paperClassName={modalClasses.modalPaper}
      footer={({ footerClass }) => (
        <div className={footerClass}>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={selectedItems.length === 0}
            data-cy="team-add-member-modal-submit"
          >
            {t('organizationAdmin:teams.add_members_modal.submit', { count: selectedItems.length })}
          </Button>

          <Button color="primary" variant="text" onClick={handleClose} data-cy="team-add-member-modal-cancel">
            {t(`common:modal.cancel`)}
          </Button>
        </div>
      )}
    >
      <div className={modalClasses.body}>
        <OutlinedInput
          value={searchQuery}
          className={classes.textField}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t(`common:label.search`)}
          data-cy="team-add-member-modal-search"
        />
        <List className={classes.list}>
          {!visibleItems.length ? (
            <ListItem key={searchQuery ? 'no results' : 'no members'} dense>
              <ListItemText
                primary={
                  searchQuery
                    ? t('organizationAdmin:teams.add_members_modal.no_search_results')
                    : t('organizationAdmin:teams.add_members_modal.no_possible_members')
                }
              />
            </ListItem>
          ) : (
            visibleItems.map(item => (
              <ListItem
                key={item.id}
                dense
                button
                onClick={handleToggle(item)}
                aria-label={t(`common:label.select_user`, { id: item.id })}
              >
                <ListItemAvatar>
                  {item.type === 'user' ? (
                    <Avatar
                      src={UserApiService.getUserByIdThumbnailUrl({ userId: item.id })}
                      alt={`${item.firstName} ${item.lastName}`}
                    />
                  ) : (
                    <GroupIcon style={{ color: CIColors.Tertiary }} />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primary={item.type === 'user' ? `${item.firstName} ${item.lastName}` : item.displayName}
                  secondary={item.type === 'user' ? item.email : item.identifier}
                />
                <ListItemIcon className={classes.listItemIcon}>
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={selectedItems.some(({ type, id }) => type === item.type && id === item.id)}
                    tabIndex={-1}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    disableRipple
                    data-cy={`team-add-member-item-${
                      item.type === 'user' ? `${item.firstName}-${item.lastName}` : item.identifier
                    }`}
                  />
                </ListItemIcon>
              </ListItem>
            ))
          )}
        </List>
      </div>
    </HUModal2>
  );
});
